import {Component, HostListener, OnDestroy, OnInit, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {AppUrl, WebApplicationsService} from '@summit/application-list';

import {Subject, take, throwError, timer} from 'rxjs';

import {HttpLoginRequest} from 'app/models/http-login-request';
import {VerificationResult} from 'app/models/verification-result.enum';
import {WebUserLoginAttemptInfo} from 'app/models/webuser-login-attempt-Info';

import {AppUtilService} from 'app/services/app-util.service';
import {AuthenticationService} from 'app/services/authentication.service';

import {environment} from 'environments/environment';
import {catchError, takeUntil} from 'rxjs/operators';
import {FormControl, FormGroup, Validators} from '@angular/forms';

const errorAnimations = [
  trigger('flyInOut', [
    state('INVALID', style({opacity: 1})),
    state('VALID', style({opacity: 0})),
    state('void', style({opacity: 0})),
    transition('* => INVALID', [
      style({transform: 'translateX(100%)'}),
      animate('0.3s ease'),
    ]),
    transition('INVALID => VALID', animate('0.3s ease'))
  ])
];

interface AuthForm {
  authUser: FormControl<string>,
  authPass: FormControl<string>
};

@Component({
  selector: 'login',
  templateUrl: './login.template.html',
  styleUrls: ['./login.template.scss', '../app.scss'],
  animations: errorAnimations
})
export class LoginComponent implements OnInit, OnDestroy {

  // Verification Results
  public verificationResult: VerificationResult;

  // Authentication Form
  public authForm: FormGroup<AuthForm> = new FormGroup<AuthForm>({
    authUser: new FormControl<string>('', {
      validators: [Validators.required, Validators.email, Validators.maxLength(100)],
      nonNullable: true
    }),
    authPass: new FormControl<string>('', {
      validators: [Validators.required, Validators.maxLength(100)],
      nonNullable: true
    })
  });

  public unsuccessfulLoginCounter = 0;
  public remainingLockoutTime = 600;
  public failedAttempt = false;
  public loginLoading = false;
  public animationTrigger: 'VALID' | 'INVALID' = 'VALID';
  public hide = signal(true);


  private unsubscribe$ = new Subject<boolean>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private webAppsService: WebApplicationsService,
    private appUtil: AppUtilService) {
  }

  public ngOnInit() {
    this.appUtil.setTitle('Log In');
    this.loginLoading = false;
    const fail: string = this.route.snapshot.params['fail'];
    this.failedAttempt = !!fail;
    if (this.failedAttempt) {
      this.loginFailedAttempt();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  public ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  // Angular's example https://material.angular.io/components/form-field/overview#form-field-prefix-suffix
  public passwordVisibilityIconClicked(event: MouseEvent) {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }

  public onSubmit(): void {
    const authUser = this.authForm.getRawValue().authUser.toLowerCase();
    const authPass = this.authForm.getRawValue().authPass;
    this.authService.request = new HttpLoginRequest(authUser);
    this.authService.jpassword = authPass;

    if (this.authForm.valid) {
      this.loginLoading = true;
      this.authService.verify()
        .pipe(
          take(1),
          catchError((err) => {
            console.error(err);
            return throwError(() => new Error(err));
          })
        )
        .subscribe((verification: VerificationResult) => {

            // Set verification result
            this.verificationResult = verification;

            // Handle switch cases
            switch (verification) {

              case VerificationResult.TEMP_PASSWORD:
                this.router.navigate(['forgotPassword'], {queryParams: {email: authUser}});
                break;

              case VerificationResult.SESSIONEXPIRED:
                this.router.navigate(['sessionExpired']);
                break;

              case VerificationResult.SUSPENDED:
                this.router.navigate(['suspended'], {queryParams: {email: authUser}});
                break;

              case VerificationResult.AUTHENTICATED:
                this.webAppsService.getAppUrl('BUSINESS_CENTER', [], environment.restfulServices, environment.production)
                  .subscribe((appUrl: AppUrl) => window.open(appUrl.url, appUrl.openTarget));
                break;

              case VerificationResult.NOACTIVEACCOUNTS:
                this.router.navigate(['suspended'], {queryParams: {email: authUser}});
                break;

              case VerificationResult.TRUSTED:
                this.authService.userLogin(authUser, authPass)
                  .pipe(
                    take(1),
                    catchError((err) => {
                      console.error(err);
                      return throwError(() => new Error(err));
                    })
                  )
                  .subscribe((userLogin) => {
                    this.authService.setChatRequirements();
                    window.location.href = decodeURIComponent(userLogin.url);
                  });
                break;

              case VerificationResult.VERIFY:
                this.authService.generateOneTimePasscodeEmail()
                  .pipe(
                    take(1),
                    catchError((err) => {
                      console.error(err);
                      return throwError(() => new Error(err));
                    })
                  )
                  .subscribe(securityCode => {
                    this.router.navigate(['verify']);
                  });
                break;

              default:
                if (!this.failedAttempt) {
                  this.router.navigate(['login/fail']);
                } else {
                  this.loginLoading = false;
                  this.loginFailedAttempt();
                }
                break;
            }
          });
    }
  }

  public goToUserProfileNew(): void {
    this.webAppsService.getAppUrl('USER_PROFILE_NEW', [], environment.restfulServices, environment.production)
      .subscribe((appUrl: AppUrl) => window.location.href = appUrl.url);
  }

  public goToHomePage(): void {
    window.location.href = '/site';
  }

  private loginFailedAttempt(): void {
    if (!this.authService.request) {
      this.router.navigateByUrl('/suspended', {skipLocationChange: true}).then(() => {
        this.router.navigate(['login']);
      })
    } else {

      // Update form values
      this.authForm.patchValue({
        authUser: this.authService.request.userLogin,
        authPass: this.authService.jpassword
      })

      this.animationTrigger = 'INVALID';

      this.authService.getLoginAttempt()
        .pipe(
          take(1),
          catchError((err) => {
            console.error(err);
            return throwError(() => new Error(err));
          })
        )
        .subscribe((data) => {
            const loginAttemptInfo: WebUserLoginAttemptInfo = data;
            if (loginAttemptInfo) {
              this.unsuccessfulLoginCounter = loginAttemptInfo.unsuccessfulLoginCounter;
              this.remainingLockoutTime = loginAttemptInfo.remainingLockoutTime;
              if (this.unsuccessfulLoginCounter > 3) {
                this.createLockOutCounter();
              }
            }
          });
    }
  }

  private createLockOutCounter(): void {
    timer(1000, 1000)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.ticker());
  }

  private async ticker() {
    this.remainingLockoutTime -= 1;
    if (this.remainingLockoutTime < 1) {
      await this.router.navigateByUrl('/suspended', {skipLocationChange: true})
      await this.router.navigate(['login']);
    }
  }

}
